import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React, { useEffect, useContext } from 'react';
import { EqualHeightProvider, Carousel, CarouselItem, ModalProvider, THeadingLevel, AnalyticsTrigger } from 'telia-front-react';
import { useStores } from '../stores/RootStore';
import PrivateMobileCard, { PrivateMobileCardProps } from './PrivateMobileCard';
import PrivateMobileDetailsModal from './details/PrivateMobileDetailsModal';
import MobileLifeOrderModal from './MobileLifeOrderModal';

export interface PrivateMobileCardsAdditionalOptions {
  name: string;
  jsx: string;
}

export interface PrivateMobileCardsProps {
  mobileLifePackages: PrivateMobileCardProps[];
  formName: string;
  headingLevel?: THeadingLevel;
  additionalOptions?: PrivateMobileCardsAdditionalOptions[];
}

const PrivateMobileCards = observer((props: PrivateMobileCardsProps) => {
  const translate = useContext(TranslationsContext).translate;
  const { ssoStore, mobileLifeStore } = useStores();
  const { mobileLifePackages, headingLevel, additionalOptions } = props;
  const singlePackageIds = mobileLifePackages.map((pkg) => pkg.packageIds.single);
  const groupPackageIds = mobileLifePackages.filter((pkg) => !pkg.forSingleUserOnly).map((pkg) => pkg.packageIds.group);

  useEffect(() => {
    if (ssoStore.customerId) {
      mobileLifeStore.fetchPackagePrices(singlePackageIds, groupPackageIds);
    } else {
      mobileLifeStore.resetPackagePrices();
    }
  }, [ssoStore.customerId]);

  const getVisiblePackages = () => {
    if (mobileLifeStore.showOnlyCallPackages) {
      return mobileLifePackages.filter((mobilePackage) => mobilePackage.singleUserDistributedPrice === 0);
    }
    return mobileLifePackages;
  };

  return (
    <>
      <EqualHeightProvider groups={['card-top', 'icon', 'title-and-arguments', 'user-slider', 'price', 'cta']}>
        <Carousel
          nextArrowText={translate('carousel.next')}
          prevArrowText={translate('carousel.previous')}
          hasContainer={true}
          breakPoint={null}
          slidesToShow={1}
          responsive={[
            {
              breakPoint: 'md',
              props: { slidesToShow: 2 },
            },
            {
              breakPoint: 'lg',
              props: { slidesToShow: 3 },
            },
            {
              breakPoint: 'xl',
              props: { slidesToShow: 4 },
            },
          ]}
        >
          {getVisiblePackages().map((mobilePackage, index) => (
            <CarouselItem key={`mobile-package-carousel-item-${index}`}>
              <AnalyticsTrigger
                trigger={{
                  callback: 'onClose',
                  id: `Modal - Close details - ${mobilePackage.packageName}`,
                }}
              >
                <ModalProvider>
                  <PrivateMobileCard {...mobilePackage} headingLevel={headingLevel} />
                  <PrivateMobileDetailsModal {...mobilePackage} additionalOptionsContents={additionalOptions} />
                </ModalProvider>
              </AnalyticsTrigger>
            </CarouselItem>
          ))}
        </Carousel>
      </EqualHeightProvider>
      <MobileLifeOrderModal mobileLifePackages={mobileLifePackages.filter((mobileLifePackage) => !mobileLifePackage?.forSingleUserOnly)} />
    </>
  );
});

export default PrivateMobileCards;
